import type { PromoCodeData } from '@/services/iframeMessenger';

export type PromoCodeErrorMessageKey = 'invalid' | 'expired' | 'inactive';

export interface PromoCodeState {
  promoCodeName: string;
  expanded: boolean;
  error: string;
  success: boolean;
  discountAmount: number;
  discountPercentage: number;
}

export const initialPromoCodeState = (): PromoCodeState => ({
  promoCodeName: '',
  expanded: false,
  error: '',
  success: false,
  discountAmount: 0,
  discountPercentage: 0,
});

/**
 * Uppercase the promo code when `checkout_promo_force_upper_case` is ON, so
 * the field displays and the BE receives the same casing the coupon was
 * issued with. Identity when the flag is OFF.
 */
export const normalizePromoCode = (value: string, forceUpperCase: boolean): string =>
  forceUpperCase ? value.toUpperCase() : value;

export const toAppliedPromoCodeData = (state: PromoCodeState): PromoCodeData | null => {
  if (!state.success) return null;

  return {
    name: state.promoCodeName,
    discountAmount: state.discountAmount,
    discountPercentage: state.discountPercentage,
  };
};

export const promoCodeStateFromApplied = (data: PromoCodeData): PromoCodeState => ({
  promoCodeName: data.name,
  expanded: true,
  error: '',
  success: true,
  discountAmount: data.discountAmount,
  discountPercentage: data.discountPercentage,
});

export const getPromoCodeErrorMessageKey = (error: string): PromoCodeErrorMessageKey => {
  switch (error) {
    case 'Invalid promo code':
    case 'Promo code is not available':
      return 'invalid';
    case 'Promo code has expired':
      return 'expired';
    case 'Promo code is not yet available':
    case 'Promo code is no longer available':
      return 'inactive';
    default:
      return 'invalid';
  }
};
